import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import TopNav from "../components/TopNav";
import SubTopicView from "../components/SubTopicView";
import Quiz from "../components/Quiz";
import { getModule } from "../data/course";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  CheckCircle2,
  Circle,
  Bookmark,
} from "lucide-react";

export default function Module() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const { user, progress, toggleBookmark, markSubtopicComplete } = useApp();
  const nav = useNavigate();

  useEffect(() => {
    if (!user) nav("/");
  }, [user, nav]);

  const mod = getModule(id);
  const mode = params.get("view"); // "quiz" or null
  const subId = params.get("sub") || mod?.subTopics[0]?.id;

  useEffect(() => {
    // Ensure URL reflects a sub topic so deep-links work
    if (mod && !mode && !params.get("sub")) {
      setParams({ sub: mod.subTopics[0].id }, { replace: true });
    }
  }, [mod, mode]); // eslint-disable-line

  const activeSub = useMemo(
    () => mod?.subTopics.find((s) => s.id === subId) || mod?.subTopics[0],
    [mod, subId]
  );

  if (!mod) {
    return (
      <div className="min-h-screen bg-background">
        <TopNav />
        <div className="p-10 text-center">Module not found.</div>
      </div>
    );
  }

  const completedSubs = progress?.completed_subtopics || [];
  const bookmarks = progress?.bookmarks || [];

  const subtopicIndex = mod.subTopics.findIndex((s) => s.id === activeSub.id);
  const isLastSub = subtopicIndex === mod.subTopics.length - 1;

  const onNextSub = () => {
    if (!isLastSub) {
      setParams({ sub: mod.subTopics[subtopicIndex + 1].id });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setParams({ view: "quiz" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const onPrevSub = () => {
    if (subtopicIndex > 0) {
      setParams({ sub: mod.subTopics[subtopicIndex - 1].id });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="module-page">
      <TopNav />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-6 text-sm flex items-center gap-2">
          <Link to="/home" className="text-muted-foreground hover:text-foreground underline">
            Home
          </Link>
          <span className="text-muted-foreground">/</span>
          <span className="mono text-xs uppercase tracking-widest text-muted-foreground">
            Module {mod.number}
          </span>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-8">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-[88px] self-start">
            <div className="rounded-md border border-border bg-card p-5">
              <span className="mono text-[10px] uppercase tracking-widest text-accent">
                Module {mod.number} • {mod.duration}
              </span>
              <h2 className="serif text-2xl leading-snug mt-1">{mod.title}</h2>
              <p className="text-xs text-muted-foreground mt-2">{mod.tagline}</p>
            </div>

            <nav className="mt-4 rounded-md border border-border bg-card overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Sub-topics
                </span>
                <span className="mono text-[10px] text-muted-foreground">
                  {mod.subTopics.length}
                </span>
              </div>
              <ScrollArea className="max-h-[50vh]">
                <ul>
                  {mod.subTopics.map((s, i) => {
                    const isActive = !mode && s.id === activeSub.id;
                    const done = completedSubs.includes(s.id);
                    const bm = bookmarks.includes(s.id);
                    return (
                      <li key={s.id}>
                        <button
                          onClick={() => setParams({ sub: s.id })}
                          className={`w-full text-left px-4 py-3 flex items-start gap-2 border-b border-border last:border-b-0 transition-colors ${
                            isActive ? "bg-primary/10" : "hover:bg-secondary"
                          }`}
                          data-testid={`sub-nav-${s.id}`}
                        >
                          {done ? (
                            <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                          ) : (
                            <Circle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm leading-snug truncate flex items-center gap-1">
                              {s.title}
                              {bm && <Bookmark className="w-3 h-3 text-accent fill-accent shrink-0" />}
                            </div>
                            <div className="mono text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                              <Clock className="w-3 h-3" /> {s.readingTime}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
              <button
                onClick={() => setParams({ view: "quiz" })}
                className={`w-full text-left px-4 py-3 border-t border-border flex items-center justify-between ${
                  mode === "quiz" ? "bg-accent/10" : "hover:bg-secondary"
                }`}
                data-testid="goto-quiz-btn"
              >
                <span className="text-sm font-medium">Module Quiz</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </nav>
          </aside>

          {/* Content */}
          <section className="min-w-0 pb-20">
            {mode === "quiz" ? (
              <Quiz key={mod.id} module={mod} onFinished={() => nav("/home")} />
            ) : (
              <>
                <SubTopicView
                  moduleNumber={mod.number}
                  sub={activeSub}
                  bookmarked={bookmarks.includes(activeSub.id)}
                  completed={completedSubs.includes(activeSub.id)}
                  onToggleBookmark={() => toggleBookmark(activeSub.id)}
                  onMarkDone={() => markSubtopicComplete(activeSub.id)}
                />

                <div className="mt-10 flex items-center justify-between gap-3">
                  <Button
                    variant="outline"
                    onClick={onPrevSub}
                    disabled={subtopicIndex === 0}
                    data-testid="prev-sub-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" /> Previous
                  </Button>
                  <Button onClick={onNextSub} data-testid="next-sub-btn">
                    {isLastSub ? "Take Quiz" : "Next subtopic"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
